/**
 * Auth0 -> Cognito Identity Pool credential exchange (Phase 2 #5B).
 *
 * After Universal Login completes, the SPA holds an Auth0 id_token. We feed
 * that to GetId + GetCredentialsForIdentity to get short-lived AWS creds
 * scoped to whichever IAM role the post-login Action's
 * `https://re-platform/primary_role` claim mapped to (RoleMapping in
 * AuthStack.ts).
 *
 * Env vars:
 *
 *   VITE_COGNITO_IDENTITY_POOL_ID=us-east-1:<uuid>
 *   VITE_AWS_REGION=us-east-1
 *
 * Identity Pool ID comes from CDK output `IdentityPoolId` on AuthStack.
 *
 * The Cognito creds are *separate* from the API session token (which lives
 * in session.ts). API calls keep using the bearer token; AWS SDK calls (S3
 * presigned PUT in Phase 2 #5B.4) use these creds. Both expire — refresh is
 * a re-login for now; refresh-token plumbing is deferred.
 */

import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
  GetIdCommand,
} from "@aws-sdk/client-cognito-identity";

import { auth0LoginsKey } from "./auth0Client";

const identityPoolId = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID as
  string | undefined;
const region =
  (import.meta.env.VITE_AWS_REGION as string | undefined) ?? "us-east-1";

export interface CognitoCreds {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  /** Unix seconds. */
  expiresAt: number;
  /** Echoed back so callers can confirm which role Cognito picked. */
  identityId: string;
}

export function isCognitoConfigured(): boolean {
  return Boolean(identityPoolId);
}

export async function exchangeAuth0IdTokenForCognitoCreds(
  idToken: string,
): Promise<CognitoCreds> {
  if (!identityPoolId) {
    throw new Error(
      "Cognito not configured — set VITE_COGNITO_IDENTITY_POOL_ID",
    );
  }
  const client = new CognitoIdentityClient({ region });
  const logins = { [auth0LoginsKey()]: idToken };

  const { IdentityId } = await client.send(
    new GetIdCommand({
      IdentityPoolId: identityPoolId,
      Logins: logins,
    }),
  );
  if (!IdentityId) {
    throw new Error("Cognito GetId returned no IdentityId");
  }

  const { Credentials } = await client.send(
    new GetCredentialsForIdentityCommand({
      IdentityId,
      Logins: logins,
    }),
  );
  if (
    !Credentials?.AccessKeyId ||
    !Credentials.SecretKey ||
    !Credentials.SessionToken ||
    !Credentials.Expiration
  ) {
    throw new Error(
      "Cognito GetCredentialsForIdentity returned incomplete creds",
    );
  }
  return {
    accessKeyId: Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken: Credentials.SessionToken,
    expiresAt: Math.floor(Credentials.Expiration.getTime() / 1000),
    identityId: IdentityId,
  };
}
